import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { formatMoney, PAYMENT_LABEL } from "@/lib/api";
import { renderBrandedHeader, renderBrandedFooter, brandedTableHeadStyles, drawSignatureBlock } from "@/lib/pdfBranding";

const COLORS = {
  primary: [27, 67, 50],
  text: [26, 28, 26],
  muted: [110, 115, 110],
  border: [209, 209, 203],
  ok: [22, 101, 52],
  bad: [180, 35, 24],
};

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("es-CO", { dateStyle: "medium", timeStyle: "short" });
}
function fmtTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("es-CO", { hour: "2-digit", minute: "2-digit" });
}

export function generateCashboxPdf({ session, txns, summary, branding }) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const W = doc.internal.pageSize.getWidth();
  const margin = 48;
  const sessionLabel = session.session_number ? `Sesión #${session.session_number} · ${session.date}` : session.date;
  let y = renderBrandedHeader(doc, branding, { title: "CIERRE DE CAJA", subtitle: sessionLabel });

  // Datos de sesión
  doc.setTextColor(...COLORS.text);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("Datos de la sesión", margin, y);
  y += 14;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.muted);
  const lines = [
    ["Fecha", session.date],
    ["Apertura", `${fmtDateTime(session.opened_at)} · por ${session.opened_by_user?.name || "—"}`],
    ["Cierre", session.closed_at ? `${fmtDateTime(session.closed_at)} · por ${session.closed_by_user?.name || "—"}` : "Sesión aún abierta"],
    ["Estado", session.status === "cerrada" ? "CERRADA" : "ABIERTA"],
  ];
  lines.forEach(([k, v]) => {
    doc.setTextColor(...COLORS.muted);
    doc.text(k.toUpperCase(), margin, y);
    doc.setTextColor(...COLORS.text);
    doc.setFont("helvetica", "bold");
    doc.text(String(v), margin + 90, y);
    doc.setFont("helvetica", "normal");
    y += 14;
  });

  y += 8;

  // Totales
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...COLORS.text);
  doc.text("Resumen", margin, y);
  y += 12;

  const totals = [
    ["Apertura", formatMoney(summary.opening || 0)],
    ["Ingresos", formatMoney(summary.income || 0)],
    ["Gastos", `- ${formatMoney(summary.expense || 0)}`],
    ["Esperado total", formatMoney(summary.expected || 0)],
    ["Total contado", formatMoney(summary.counted ?? 0)],
    ["Diferencia", `${(summary.difference || 0) >= 0 ? "+" : ""}${formatMoney(summary.difference || 0)}`],
  ];
  autoTable(doc, {
    startY: y,
    head: [["Concepto", "Monto"]],
    body: totals,
    theme: "grid",
    headStyles: { fillColor: COLORS.primary, textColor: 255, fontSize: 9 },
    bodyStyles: { fontSize: 10, textColor: COLORS.text },
    columnStyles: { 1: { halign: "right", fontStyle: "bold" } },
    margin: { left: margin, right: margin },
  });
  y = doc.lastAutoTable.finalY + 16;

  // Cuadre por método
  const expByMethod = summary.expected_by_method || {};
  const cntByMethod = summary.counted_by_method || {};
  const methods = Object.keys(expByMethod);
  if (methods.length > 0) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("Cuadre por método de pago", margin, y);
    y += 6;
    const rows = methods.map((m) => {
      const exp = Number(expByMethod[m] || 0);
      const cnt = Number(cntByMethod[m] || 0);
      const diff = cnt - exp;
      return [
        PAYMENT_LABEL[m] || m,
        formatMoney(exp),
        formatMoney(cnt),
        `${diff >= 0 ? "+" : ""}${formatMoney(diff)}`,
      ];
    });
    autoTable(doc, {
      startY: y + 4,
      head: [["Método", "Esperado", "Contado", "Diferencia"]],
      body: rows,
      theme: "grid",
      headStyles: brandedTableHeadStyles(branding),
      bodyStyles: { fontSize: 9, textColor: COLORS.text },
      columnStyles: { 1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right", fontStyle: "bold" } },
      margin: { left: margin, right: margin },
    });
    y = doc.lastAutoTable.finalY + 16;
  }

  // Movimientos
  if (txns && txns.length > 0) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(`Movimientos (${txns.length})`, margin, y);
    y += 6;
    autoTable(doc, {
      startY: y + 4,
      head: [["Hora", "Tipo", "Descripción", "Método", "Monto"]],
      body: txns.map((t) => [
        fmtTime(t.created_at),
        (t.type || "").toUpperCase(),
        (t.description || "—").slice(0, 60),
        PAYMENT_LABEL[t.payment_method] || t.payment_method || "—",
        `${t.type === "gasto" ? "-" : "+"}${formatMoney(t.amount || 0)}`,
      ]),
      theme: "striped",
      headStyles: { ...brandedTableHeadStyles(branding), fontSize: 8 },
      bodyStyles: { fontSize: 8, textColor: COLORS.text },
      columnStyles: { 4: { halign: "right" } },
      margin: { left: margin, right: margin },
    });
    y = doc.lastAutoTable.finalY + 16;
  }

  // Justificación
  if (session.closing_notes) {
    if (y > 640) { doc.addPage(); y = margin; }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("Justificación / Observaciones del cierre", margin, y);
    y += 12;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    const wrapped = doc.splitTextToSize(session.closing_notes, W - 2 * margin);
    doc.text(wrapped, margin, y);
    y += wrapped.length * 11 + 12;
  }

  // Firmas
  if (y > 600) { doc.addPage(); y = margin + 60; }
  else y += 60;

  const sigW = (W - 2 * margin - 30) / 2;
  const opener = session.opened_by_user || {};
  const closer = session.closed_by_user || {};
  drawSignatureBlock(doc, {
    x: margin,
    baselineY: y,
    width: sigW,
    signatureImage: opener.signature_image || null,
    label: "Recepción / Cajero (apertura)",
    name: opener.name || "",
    subtitle: opener.signature_text || opener.registry_number || "",
  });
  drawSignatureBlock(doc, {
    x: margin + sigW + 30,
    baselineY: y,
    width: sigW,
    signatureImage: closer.signature_image || null,
    label: "Supervisor / Cierre",
    name: closer.name || "",
    subtitle: closer.signature_text || closer.registry_number || "",
  });

  // Footer
  renderBrandedFooter(doc, branding);

  const filename = `cierre_caja_${session.date}_${session.session_number || 1}.pdf`;
  doc.save(filename);
}
